import {
  AnyHash,
  FieldConfig,
  FieldDataTypes,
  isFieldDateType,
  nString,
  StandardQueryParams,
  StringHash,
  uString,
} from '@hidat/huijs-common';
import {
  GetFieldsConfigInterface,
  ResourceConfig,
} from '../resource-configs/resource-config';
import { FieldConverter, JDateUtils } from '../utils';

export interface SearchFilterFieldConfig {
  name?: string;
  dataType?: FieldDataTypes;
  urlParam?: string;
  fieldConfig?: FieldConfig;
}

/**
 * Filter Field Config
 * Configuration structure used to define how a set of fields are serialized between url query parameters,
 * class field names, and JSON objects.
 * Basically a light weight FieldConfig, which can be generated from a FieldConfig or on its own...
 */
export class SearchFilterField {
  public readonly name: string;
  public readonly dataType: FieldDataTypes;
  public readonly urlParam: string;
  public fieldConfig?: FieldConfig;

  constructor(
    nameOrFieldConfig: string | FieldConfig | SearchFilterFieldConfig,
    dataType = FieldDataTypes.STRING,
    urlParm?: string,
  ) {
    if (nameOrFieldConfig instanceof FieldConfig) {
      this.fieldConfig = nameOrFieldConfig;
      this.name = nameOrFieldConfig.name;
      this.dataType = nameOrFieldConfig.dataType;
      this.urlParam = this.name;
    } else if (typeof nameOrFieldConfig === 'string') {
      this.name = nameOrFieldConfig;
      this.dataType = dataType;
      this.urlParam = urlParm ? urlParm : nameOrFieldConfig;
    } else {
      let name = nameOrFieldConfig.name;
      let dataType: FieldDataTypes | undefined;
      if (nameOrFieldConfig.fieldConfig) {
        this.fieldConfig = nameOrFieldConfig.fieldConfig;
        dataType = this.fieldConfig.dataType;
        if (!name) {
          name = this.fieldConfig.name;
        }
      }
      if (name) {
        this.name = name;
        this.dataType =
          nameOrFieldConfig.dataType ?? dataType ?? FieldDataTypes.STRING;
        this.urlParam = nameOrFieldConfig.urlParam ?? this.name;
      } else {
        throw new Error('Filter Field has no name!');
      }
    }
  }
}

/**
 * Filter Config
 * Defines and implements serialization strategies for local URL's and Query Builder parameters.
 * This may be better termed as a 'transformer', as that is the heart of what it does.
 * There are two aims.
 *   First off, dealing with $@##$@ dates.
 *   Secondly, being able to use short codes for browser URL params, which can then be transformed into API facing params.
 */
export class SearchFilterConfig {
  protected _fields: SearchFilterField[] = [];
  public readonly resourceVocabCode: uString;

  /**
   * Constructor
   * FieldsOrResourceConfig can be one of the following:
   * * Undefined - only the 'q' config will be added
   * * Boolean - if false, then 'q' will not be added
   * * Field or Filter Config Array - All the items are added to the filter
   * * ResourceConfig - All the fields of the resource config will be added, unless 'fieldsToInclude' is defined, then only the given fields will be added.
   * @param fieldsOrResourceConfig
   * @param fieldsToInclude
   */
  constructor(
    fieldsOrResourceConfig?:
      | (SearchFilterField | FieldConfig | undefined)[]
      | ResourceConfig
      | boolean,
    fieldsToInclude?: GetFieldsConfigInterface,
  ) {
    let fields: (SearchFilterField | FieldConfig | undefined)[] | undefined;
    let addQ = true;
    if (fieldsOrResourceConfig !== undefined) {
      if (fieldsOrResourceConfig instanceof ResourceConfig) {
        this.resourceVocabCode = fieldsOrResourceConfig.vocabCode;

        if (fieldsToInclude) {
          const foundFields = fieldsOrResourceConfig.getFields(fieldsToInclude);
          if (foundFields) {
            fields = foundFields;
          }
        } else {
          fields = fieldsOrResourceConfig.fields;
        }
      } else if (typeof fieldsOrResourceConfig === 'boolean') {
        addQ = fieldsOrResourceConfig;
      } else {
        fields = fieldsOrResourceConfig;
      }

      if (fields) {
        for (const f of fields) {
          if (f) {
            if (f instanceof SearchFilterField) {
              this._fields.push(f);
            } else {
              this._fields.push(new SearchFilterField(f));
            }
          }
        }
      }
    }
    // If no fields, add in q (but not startsWith)
    if (this._fields.length === 0 && addQ) {
      this.addQ();
    }
  }

  /**
   * Adds the standard 'q' full text string search field to the configuration
   * @param fieldConfig
   */
  public addQ(fieldConfig?: FieldConfig): SearchFilterConfig {
    if (fieldConfig) {
      this._fields.push(
        new SearchFilterField(
          fieldConfig,
          FieldDataTypes.STRING,
          StandardQueryParams.SEARCH_QUERY,
        ),
      );
    } else {
      this._fields.push(
        new SearchFilterField(StandardQueryParams.SEARCH_QUERY),
      );
    }
    return this;
  }

  /**
   * Adds a 'starts with' filter field to the configuration
   * @param fieldConfig
   */
  public addStartsWith(fieldConfig?: FieldConfig): SearchFilterConfig {
    if (fieldConfig) {
      this._fields.push(
        new SearchFilterField(
          fieldConfig,
          FieldDataTypes.STRING,
          StandardQueryParams.STARTS_WITH,
        ),
      );
    } else {
      this._fields.push(
        new SearchFilterField(
          'startsWith',
          FieldDataTypes.STRING,
          StandardQueryParams.STARTS_WITH,
        ),
      );
    }
    return this;
  }

  /**
   * Add Field
   * Adds the given field to the configuration.
   * This simply shortcuts the instantiating the FilterField yourself...
   * @param nameOrFieldConfig
   * @param dataType
   * @param urlParam  Alternate name for page URL parameter
   */
  public addField(
    nameOrFieldConfig: string | FieldConfig | SearchFilterFieldConfig,
    dataType = FieldDataTypes.STRING,
    urlParam?: string,
  ): SearchFilterConfig {
    this._fields.push(
      new SearchFilterField(nameOrFieldConfig, dataType, urlParam),
    );
    return this;
  }

  /**
   * Adds a Range Field
   * Takes the given field definition and creates two FilterFields - one with low appended and one with high appended.
   * Value inclusivity is up to the backend, but figure it will by default be inclusive
   * @param nameOrFieldConfig
   * @param dataType
   * @param urlParam
   */
  public addRangeField(
    nameOrFieldConfig: string | FieldConfig,
    dataType = FieldDataTypes.STRING,
    urlParam?: string,
  ): SearchFilterConfig {
    let baseName: string;
    let fieldConfig: FieldConfig | undefined;
    if (typeof nameOrFieldConfig === 'string') {
      baseName = nameOrFieldConfig;
    } else {
      fieldConfig = nameOrFieldConfig;
      baseName = fieldConfig.name;
      dataType = fieldConfig.dataType;
    }

    const lowName = baseName + 'Low';
    const highName = baseName + 'High';
    if (!urlParam) {
      urlParam = baseName;
    }
    const urlLow = urlParam + 'Low';
    const urlHigh = urlParam + 'High';

    this._fields.push(
      new SearchFilterField({
        name: lowName,
        dataType,
        fieldConfig,
        urlParam: urlLow,
      }),
    );
    this._fields.push(
      new SearchFilterField({
        name: highName,
        dataType,
        fieldConfig,
        urlParam: urlHigh,
      }),
    );
    return this;
  }

  /**
   * To Local Url Hash
   * Converts the given searchFilter to a set of parameters that can be used to pass parameters on the local browsers
   * query string.  This is meant to help as you move forward and backward between pages.
   * Serialized name is the fields 'urlParam'
   * @param searchFilter
   */
  toLocalUrlHash(searchFilter: SearchFilterParams | AnyHash) {
    const hash: StringHash = {};
    if (searchFilter instanceof SearchFilterParams) {
      if (searchFilter.page != null) {
        hash[StandardQueryParams.PAGE] = searchFilter.page.toString();
      }
      if (searchFilter.sorts.length > 0) {
        hash[StandardQueryParams.SORT] = searchFilter.sorts.join(',');
      }
      if (searchFilter.includes.length > 0) {
        hash[StandardQueryParams.INCLUDES] = searchFilter.includes.join(',');
      }
    }

    if (this._fields) {
      for (const field of this._fields) {
        const val = searchFilter[
          field.name as keyof (SearchFilterParams | AnyHash)
        ] as unknown;
        let res: nString;
        if (val != null) {
          if (field.dataType.startsWith('date')) {
            res = FieldConverter.dateToString(val, field.dataType);
          } else {
            res = FieldConverter.toStr(val);
          }
          if (res) {
            hash[field.urlParam] = res;
          }
        }
      }
    }
    return hash;
  }

  /**
   * To Hash
   * Converts the given searchFilter to a hash
   * @param searchFilter
   * @param whereClauseOnly
   */
  public toHash(
    searchFilter: SearchFilterParams | AnyHash,
    whereClauseOnly = false,
  ) {
    const hash: AnyHash = {};
    if (searchFilter instanceof SearchFilterParams) {
      if (!whereClauseOnly) {
        if (searchFilter.page != null) {
          hash[StandardQueryParams.PAGE] = searchFilter.page;
        }
        if (searchFilter.sorts.length > 0) {
          hash[StandardQueryParams.SORT] = searchFilter.sorts.join(',');
        }
        if (searchFilter.includes.length > 0) {
          hash[StandardQueryParams.INCLUDES] = searchFilter.includes.join(',');
        }
      }
    }
    if (this._fields) {
      for (const field of this._fields) {
        const val =
          searchFilter[field.name as keyof (SearchFilterParams | AnyHash)];
        let res: unknown;
        if (val !== undefined) {
          if (field.dataType.startsWith('date')) {
            res = FieldConverter.dateToString(val, field.dataType);
          } else {
            res = val;
          }
          if (res !== undefined) {
            hash[field.name] = res;
          }
        }
      }
    }
    return hash;
  }

  protected deserializeStandardFields(hash: AnyHash): AnyHash {
    const searchFilter = new SearchFilterParams();
    const pp = hash[StandardQueryParams.PAGE];
    if (pp != null) {
      searchFilter.page = FieldConverter.toInt(pp);
    }
    if (hash[StandardQueryParams.SORT]) {
      searchFilter.sorts = hash[StandardQueryParams.SORT].split(',');
    }
    if (hash[StandardQueryParams.INCLUDES]) {
      searchFilter.includes = hash[StandardQueryParams.INCLUDES].split(',');
    }

    return searchFilter;
  }

  protected deserializeHash(
    jsonParams: AnyHash,
    includeStandardParams: boolean,
    convertToJsDates: boolean,
  ): AnyHash {
    let hash: AnyHash;
    if (includeStandardParams) {
      hash = this.deserializeStandardFields(jsonParams);
    } else {
      hash = {};
    }

    for (const field of this._fields) {
      const val = jsonParams[field.name];
      if (val != null) {
        let res = FieldConverter.convertField(val, field.dataType);
        if (convertToJsDates && isFieldDateType(field.dataType)) {
          res = JDateUtils.toJSDate(res);
        }
        if (res != null) {
          hash[field.name] = res;
        }
      }
    }

    return hash;
  }

  /**
   * Deserializes the given hash using the URL fields
   * @param urlParams
   */
  fromLocalUrlHash(urlParams: AnyHash): AnyHash {
    const hash = this.deserializeStandardFields(urlParams);
    if (this._fields) {
      for (const field of this._fields) {
        const val = urlParams[field.urlParam];
        if (val != null) {
          const res = FieldConverter.convertField(val, field.dataType);
          if (res != null) {
            hash[field.name] = res;
          }
        }
      }
    }
    return hash;
  }

  /**
   * Deserializes the given hash using the field names, includes paging/includes
   * If convertToJsDates is true, then all 'Date' field will be converted to JS Dates - thought we needed this,
   * but turned out to be a different problem with TypeORM More/LessThan operators.
   * @param apiParams
   * @param convertToJsDates  Convert all 'Date' field types to JS Dates
   */
  fromApiParams(
    apiParams: AnyHash,
    convertToJsDates = false,
  ): AnyHash | undefined {
    return this.deserializeHash(apiParams, true, convertToJsDates);
  }

  /**
   * Deserializes the given hash using the field names, does NOT include paging/includes
   * @param formValues
   */
  fromFormValues(formValues: AnyHash): AnyHash | undefined {
    return this.deserializeHash(formValues, false, false);
  }

  /**
   * Get Form Fields
   * Returns the filter fields that should be used in a search filter form.
   */
  getFieldsForForm(): SearchFilterField[] {
    return this._fields;
  }
}

/**
 * Search/Filter Params
 * Base class for Filter/Search Params classes.
 * You will want to extend this class with your custom fields.
 * These are for convenience only.
 * Should this just be an interface?
 */
export class SearchFilterParams {
  public q?: nString;
  public startsWith?: nString;
  public sorts: string[] = [];
  public includes: string[] = [];
  page?: number;
}
